'use client';

import { API_ROUTES, type ApiError, useApiMutation } from '@shared/api-utils';
import { clearRegistrationAuthComplete, useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { useDebug } from '@shared/debug';
import { setPendingRegistrationTracking, useDataLayerTrackEvent, useFullStoryTrackEvent, useOptimoveTrackEvent, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { setRegOriginCookie, useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { FormData } from '../formConfig';
import { getRegistrationContext } from '../registrationContext';
import { buildDataLayerPlayer } from '../utils/dataLayerPlayerBuilder';
import { omitStateField, transformFormDataToApiPayload } from '../utils/payloadTransformer';
import { createRegistrationSuccessTracking, saveRegistrationSuccessTracking } from '../utils/registrationSuccessTracking';
import { resolveRegistrationAuthReturnUri } from './registrationAuthResume';
import { redirectToRegistrationLogin } from './registrationRedirect';
import type { PlayerRegistrationData, RegistrationResponse } from './types';

const REGISTRATION_PAGE_ID = 'register';

const usePlayerRegistration = () => {
  const debug = useDebug();
  const config = useClientConfig();
  const fullStoryTrackEvent = useFullStoryTrackEvent();
  const dataLayerTrackEvent = useDataLayerTrackEvent();
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const trackOptimoveRegistrationEvent = useOptimoveTrackEvent();
  const { update: updateSession, resolveProfile } = useSession();
  const searchParams = useSearchParams();
  const t = useTranslations('register');
  // TODO: Turn verificationStatus back on when we know the status of the GBG check
  // const { refetch: refetchPlayerVerification } = usePlayerVerification({ queryOptions: { enabled: false } });
  const { mutateAsync: registerPlayerMutation } = useApiMutation<RegistrationResponse, PlayerRegistrationData>(API_ROUTES.REGISTER.PLAYER_REGISTRATION, 'POST', {
    requestOptions: {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // Note: x-api-key header is added by the API service based on environment
      },
    },
    mutationOptions: {
      onSuccess: async () => {
        setRegOriginCookie();
      },
      onError: async (error, registrationData) => {
        const responseData = (error as ApiError & { responseData?: { playerId?: string; error?: string } }).responseData;

        if (responseData?.error) {
          ptsTrackEvent({
            ev: responseData.error,
            playerId: responseData.playerId || 'unknown',
            uid: 0,
            t: 'registration',
            ea: 'registration_success',
            ec: 'registration',
            page: { id: REGISTRATION_PAGE_ID },
          });
        }

        const session = await updateSession();
        const profile = session?.profile ?? null;

        fullStoryTrackEvent('Registration | Failure', {
          error: (error as Error)?.message || 'System Unavailable',
        });

        dataLayerTrackEvent({
          event: 'error',
          errorType: 'registration',
          errorMessage: t('screens.error.title'),
          player: buildDataLayerPlayer(profile, registrationData),
        });
      },
    },
  });

  const registerPlayer = useCallback(
    async (registrationData: PlayerRegistrationData, successTrackingAttributes?: Record<string, string>) => {
      const response = await registerPlayerMutation(registrationData);

      if (!response?.ott) {
        return response;
      }

      if (successTrackingAttributes) {
        saveRegistrationSuccessTracking(successTrackingAttributes);
      }

      // Where Keycloak returns the player after the login-after-registration round-trip: back to
      // /register so the flow resumes on LoadingSuccessStep. The original referrer and redirect_url
      // are carried in the URI because the full-page redirect wipes the in-memory registration context.
      const referrer = typeof document !== 'undefined' ? document.referrer : '';
      const registrationRedirectUrl = getRegistrationContext()?.registrationRedirectUrl ?? null;
      const authReturnUri = resolveRegistrationAuthReturnUri(config.baseUrl, searchParams, referrer, registrationRedirectUrl);

      redirectToRegistrationLogin(config.baseUrl, response.ott, authReturnUri);
      return null;
    },
    [registerPlayerMutation, config.baseUrl, searchParams]
  );

  // Transform form data to registration payload
  const transformFormData = useCallback((formData: Record<string, unknown>): PlayerRegistrationData => {
    if (!formData || typeof formData !== 'object') {
      throw new Error('Form data is required for registration');
    }

    const basePayload = transformFormDataToApiPayload(formData);

    const createAccount = formData.createAccount as Record<string, unknown> | undefined;
    const personalDetails = formData.personalDetails as Record<string, unknown> | undefined;
    const email = createAccount?.email as string | undefined;
    const firstname = personalDetails?.firstName as string | undefined;
    const lastname = personalDetails?.lastName as string | undefined;

    if (!email) {
      throw new Error('Email is required for registration');
    }
    if (!firstname) {
      throw new Error('First name is required for registration');
    }
    if (!lastname) {
      throw new Error('Last name is required for registration');
    }

    return omitStateField(basePayload) as PlayerRegistrationData;
  }, []);

  const runPostRegistrationTasks = useCallback(
    async (registrationData?: PlayerRegistrationData) => {
      const profile = await resolveProfile();

      if (!profile) {
        debug.forceWarn('[usePlayerRegistration] Profile fetch failed but registration succeeded');
      }

      const playerId = profile?.id || 'unknown';
      const playerNumber = profile?.nr || 'unknown';
      const displayName = `Player ${playerNumber}`;

      ptsTrackEvent({
        uid: Number(playerNumber) || 0,
        playerId,
        ev: 'success',
        t: 'registration',
        ea: 'registration_success',
        ec: 'registration',
        page: { id: REGISTRATION_PAGE_ID },
      });

      if (profile) {
        await trackOptimoveRegistrationEvent(profile);
      }

      if (playerId === 'unknown') {
        debug.forceError('[usePlayerRegistration] Missing playerProfile.id for FullStory playerId');
      }

      setPendingRegistrationTracking({
        uid: playerId,
        identityProperties: { method: 'Registration', displayName, playerNumber, playerTier: 'New Player' },
        successEvent: 'Registration | Success',
      });

      dataLayerTrackEvent({
        event: 'registration',
        player: buildDataLayerPlayer(profile, registrationData),
      });
    },
    [resolveProfile, dataLayerTrackEvent, ptsTrackEvent, trackOptimoveRegistrationEvent, debug]
  );

  const completeRegistrationAfterAuth = useCallback(async () => {
    clearRegistrationAuthComplete();
    await runPostRegistrationTasks();
  }, [runPostRegistrationTasks]);

  // Submit player registration
  const submitRegistration = useCallback(
    async (formData: Record<string, unknown>) => {
      const registrationData = transformFormData(formData);
      const successTrackingAttributes = createRegistrationSuccessTracking(formData as FormData, getSiteConfig().REGISTRATION.tracking.successAttributes, searchParams.get('code'));

      const response = await registerPlayer(registrationData, successTrackingAttributes);

      if (response === null) {
        return null;
      }

      await runPostRegistrationTasks(registrationData);

      return response;
    },
    [registerPlayer, transformFormData, runPostRegistrationTasks, searchParams]
  );

  // Handle registration from form
  const handleRegistration = useCallback(
    async (form: UseFormReturn<Record<string, unknown>>) => {
      const formData = form.getValues();
      return await submitRegistration(formData);
    },
    [submitRegistration]
  );

  return {
    handleRegistration,
    submitRegistration,
    completeRegistrationAfterAuth,
    registerPlayer,
  };
};

export { usePlayerRegistration };
export type { PlayerRegistrationData, RegistrationResponse };
